import { Analytics } from '@cobliteam/analytics';
import type { FeedItem } from '@knocklabs/client';
import { useKnockFeed } from '@knocklabs/react';
import { useCallback, useRef } from 'react';
import { useRealtimeFeedItems } from '../../../core/hooks/useNotificationFeed';
import { dismissToast, showCardToast } from '../../../core/showCardToast';
import useNotificationDrawerStore from '../../../core/store/useNotificationDrawerStore';
import { buildRiskRecognitionEventProps, RISK_RECOGNITION_EVENTS } from '../analytics';
import { getFeedButtonLabelsFromItem, getHighlightFromFeedItem, isHighlightRenderable, type RiskRecognitionFeedItem } from '../parse';
import RiskCardBody from '../RiskCardBody';

const RISK_RECOGNITION_TYPE_KEY = 'risk-recognition';
// Id fixo: reusar o mesmo toast faz cada novo evento substituir o anterior — só o último fica na tela.
const RISK_RECOGNITION_TOAST_ID = 'risk-recognition-toast';

/**
 * Controller da surface "toast" do risco (era o RiskRecognitionToastListener). Notifica novos eventos em
 * QUALQUER página (diferente do card flutuante, só /monitoring). Subscreve apenas os pushes ao vivo do
 * socket compartilhado — não o fetch da montagem — então não toasta histórico ao trocar de página; só
 * eventos genuinamente novos. Só o último evento fica visível (id fixo, sem auto-dismiss).
 */
const useRiskToastController = () => {
  const { feedClient } = useKnockFeed();
  // Dedup por id: o socket pode reemitir o mesmo item em batches diferentes — não toasta de novo.
  const toastedIdsRef = useRef<Set<string>>(new Set());

  const handleRealtime = useCallback(
    (items: FeedItem[]) => {
      for (const rawItem of items) {
        if (toastedIdsRef.current.has(rawItem.id)) continue;
        const item = rawItem as unknown as RiskRecognitionFeedItem;
        const highlight = getHighlightFromFeedItem(item);
        if (!(isHighlightRenderable(highlight, null) && highlight?.metadata)) continue;

        toastedIdsRef.current.add(item.id);
        Analytics.track(RISK_RECOGNITION_EVENTS.displayed, buildRiskRecognitionEventProps(highlight, 'toast'));
        const { action: actionLabel, dismiss: dismissLabel } = getFeedButtonLabelsFromItem(item);

        // Mesmo card do /monitoring, renderizado como toast pelo helper compartilhado (sonner, top-right).
        showCardToast(RISK_RECOGNITION_TOAST_ID, (toastId) => (
          <RiskCardBody
            highlight={highlight}
            actionLabel={actionLabel}
            dismissLabel={dismissLabel}
            className='w-[352px] max-w-[calc(100vw-2rem)]'
            onDismiss={() => {
              Analytics.track(RISK_RECOGNITION_EVENTS.dismissed, buildRiskRecognitionEventProps(highlight, 'toast'));
              dismissToast(toastId);
            }}
            onAction={() => {
              Analytics.track(RISK_RECOGNITION_EVENTS.action, buildRiskRecognitionEventProps(highlight, 'toast'));
              void feedClient.markAsInteracted(rawItem);
              useNotificationDrawerStore.getState().openDrawer(RISK_RECOGNITION_TYPE_KEY, highlight);
              dismissToast(toastId);
            }}
          />
        ));
      }
    },
    [feedClient]
  );

  useRealtimeFeedItems(handleRealtime);
};

export default useRiskToastController;
